import { CounselProMark } from '@/components'
import { cn } from '@/utils'
import type { PendingMessage } from '../optimisticMessages'
import { messageTime, type ThreadGroupRow } from '../threadTimeline'
import { PendingMessageBubble } from './PendingMessageBubble'
import { SupportPill } from './SupportPill'
import { TicketMessageBubble } from './TicketMessageBubble'

interface TicketMessageGroupProps {
  group: ThreadGroupRow
  onRetryPending: (pending: PendingMessage) => void
  onDiscardPending: (pending: PendingMessage) => void
}

const StaffAvatar = () => (
  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand-deep">
    <CounselProMark className="h-3 w-3" />
  </span>
)

const StaffGroupHeader = ({
  staffName,
  startedAt,
}: {
  staffName: string | null
  startedAt: string
}) => (
  <div className="flex h-5 items-center gap-x-2">
    {staffName && (
      <span className="truncate text-xs font-semibold text-blue-7">
        {staffName}
      </span>
    )}
    <SupportPill tone="brand">CounselPro Staff</SupportPill>
    <span className="shrink-0 text-xs text-ink-subtle">
      {messageTime(startedAt)}
    </span>
  </div>
)

export const TicketMessageGroup = ({
  group,
  onRetryPending,
  onDiscardPending,
}: TicketMessageGroupProps) => {
  const isUser = group.authorType === 'user'

  return (
    <div className={cn('flex gap-2', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && <StaffAvatar />}

      <div
        className={cn(
          'flex min-w-0 max-w-[85%] flex-col',
          isUser ? 'items-end' : 'items-start',
        )}
      >
        {isUser ? (
          <span className="flex h-5 items-center px-1 text-xs text-ink-subtle">
            {messageTime(group.startedAt)}
          </span>
        ) : (
          <StaffGroupHeader
            staffName={group.staffName}
            startedAt={group.startedAt}
          />
        )}

        <div
          className={cn(
            'mt-1 flex w-full flex-col gap-1.5',
            isUser ? 'items-end' : 'items-start',
          )}
        >
          {group.entries.map((entry) =>
            entry.kind === 'message' ? (
              <TicketMessageBubble
                key={entry.key}
                message={entry.message}
                showsLinkPreview={entry.showsLinkPreview}
              />
            ) : (
              <PendingMessageBubble
                key={entry.key}
                pending={entry.pending}
                onRetry={onRetryPending}
                onDiscard={onDiscardPending}
              />
            ),
          )}
        </div>
      </div>
    </div>
  )
}
